import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLocale } from '@platform/core';
import { useBudget } from '../contexts/BudgetContext';
import { IntervalType, SubjectType, BudgetSection } from '../types/budget.types';

// Currency symbol helper
const getCurrencySymbol = (currency: string | undefined): string => {
  switch (currency) {
    case 'USD': return '$';
    case 'EUR': return '€';
    case 'GBP': return '£';
    case 'ILS':
    default: return '₪';
  }
};

// Format number with max 3 decimal places
const formatNumber = (value: number | undefined): string => {
  if (value === undefined || value === null) return '0';
  // Round to max 3 decimal places and remove trailing zeros
  return parseFloat(value.toFixed(3)).toString();
};

// Format amount with commas, no decimals
const formatAmount = (value: number | undefined): string => {
  if (value === undefined || value === null) return '0';
  return Math.round(value).toLocaleString('en-US');
};

export const SettingsPage: React.FC = () => {
  const { t, direction } = useLocale();
  const navigate = useNavigate();
  const {
    currentBudget,
    updateBudget,
    deleteBudget,
    createBudget,
    addSubject,
    updateSubject,
    removeSubject,
    addSection,
    updateSection,
    removeSection,
    createDataCollection,
    entries,
  } = useBudget();

  const [editingName, setEditingName] = useState(false);
  const [budgetName, setBudgetName] = useState(currentBudget?.name || '');

  // State for editing subject/section names
  const [editingSubjectId, setEditingSubjectId] = useState<string | null>(null);
  const [editingSubjectName, setEditingSubjectName] = useState('');
  const [editingSectionId, setEditingSectionId] = useState<string | null>(null);
  const [editingSectionName, setEditingSectionName] = useState('');

  // Local state for editing values (save on blur, not on every keystroke)
  const [editingValues, setEditingValues] = useState<Record<string, string>>({});

  const getEditingValue = (id: string, currentValue: number | undefined): string => {
    if (id in editingValues) return editingValues[id];
    return currentValue?.toString() || '';
  };

  const setEditingValue = (id: string, value: string) => {
    setEditingValues(prev => ({ ...prev, [id]: value }));
  };

  const clearEditingValue = (id: string) => {
    setEditingValues(prev => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  // Group sections by subject
  const sectionsBySubject = useMemo(() => {
    if (!currentBudget) return {};
    const grouped: Record<string, BudgetSection[]> = {};
    currentBudget.sections.forEach(section => {
      if (!grouped[section.subjectId]) {
        grouped[section.subjectId] = [];
      }
      grouped[section.subjectId].push(section);
    });
    return grouped;
  }, [currentBudget]);

  // Calculate total goals for each subject from its sections
  const subjectGoalTotals = useMemo(() => {
    const totals: Record<string, number> = {};
    Object.entries(sectionsBySubject).forEach(([subjectId, sections]) => {
      totals[subjectId] = sections.reduce((sum, s) => {
        const goal = parseFloat(String(s.incomePartGoal)) || 0;
        return sum + goal;
      }, 0);
    });
    return totals;
  }, [sectionsBySubject]);

  // Calculate total income - sum IncomePartGoal from Credit subjects (same as Vue)
  const totalIncome = useMemo(() => {
    if (!currentBudget) return 0;
    // Vue: budget.Subjects.filter(s=>s.Type=='Credit').map(x=>x.IncomePartGoal).reduce(...)
    return currentBudget.subjects
      .filter(s => s.type === 'credit')
      .reduce((sum, s) => sum + (s.goalPercentage || 0), 0);
  }, [currentBudget]);

  // Calculate number of periods based on date range and interval type
  const periodsCount = useMemo(() => {
    if (!currentBudget?.startDate || !currentBudget?.endDate) {
      // Default: 12 months or 52 weeks
      return currentBudget?.intervalType === 'weekly' ? 52 : 12;
    }
    const start = new Date(currentBudget.startDate);
    const end = new Date(currentBudget.endDate);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (currentBudget.intervalType === 'weekly') {
      return Math.max(1, Math.ceil(diffDays / 7));
    } else {
      // Monthly: calculate months between dates
      const months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth()) + 1;
      return Math.max(1, months);
    }
  }, [currentBudget?.startDate, currentBudget?.endDate, currentBudget?.intervalType]);

  // Get planned amounts from existing entries (if data collection was already created)
  // Key insight: entries store section NAME in sectionId field, not section ID
  const plannedAmountsBySection = useMemo(() => {
    if (!currentBudget || !entries.length) return {};

    // Get the planned measure name (Hebrew: מתוכנן)
    const measures = currentBudget.measures || ['בפועל', 'מתוכנן'];
    const plannedMeasure = measures[1] || 'מתוכנן';
    const plannedMeasureName = typeof plannedMeasure === 'string' ? plannedMeasure : (plannedMeasure as any).Name || 'מתוכנן';

    // Helper to check if an entry is for the planned measure
    const isPlannedMeasure = (m: string) => m === plannedMeasureName || m === 'מתוכנן' || m === 'budget';

    // Map: section name -> total planned amount (summed across all periods)
    const amounts: Record<string, number> = {};

    // Sum planned entries for each section BY NAME (entries use section name as sectionId)
    currentBudget.sections.forEach(section => {
      const sectionEntries = entries.filter(e =>
        ((e as any).section === section.name || e.sectionId === section.name) &&
        isPlannedMeasure(e.measure)
      );
      // Get amount from first entry (they should all be the same per period)
      if (sectionEntries.length > 0) {
        amounts[section.name] = sectionEntries[0].amount;
      }
    });

    // Also calculate for subjects without sections
    currentBudget.subjects.forEach(subject => {
      const subjectSections = currentBudget.sections.filter(s => s.subjectId === subject.id);
      if (subjectSections.length === 0) {
        const subjectEntries = entries.filter(e =>
          (e.subjectId === subject.id || e.subjectId === subject.name || (e as any).subject === subject.name) &&
          isPlannedMeasure(e.measure)
        );
        if (subjectEntries.length > 0) {
          amounts[`subject-${subject.name}`] = subjectEntries[0].amount;
        }
      }
    });

    return amounts;
  }, [currentBudget, entries]);

  // Calculate subject totals from existing planned entries
  // Uses section NAME for lookup since that's what entries store
  const plannedAmountsBySubject = useMemo(() => {
    if (!currentBudget) return {};
    const amounts: Record<string, number> = {};

    currentBudget.subjects.forEach(subject => {
      const subjectSections = currentBudget.sections.filter(s => s.subjectId === subject.id);
      if (subjectSections.length > 0) {
        // Sum up all section planned amounts for this subject (lookup by section NAME)
        amounts[subject.id] = subjectSections.reduce((sum, section) => {
          return sum + (plannedAmountsBySection[section.name] || 0);
        }, 0);
      } else {
        // Use subject-level entry if available (lookup by subject NAME)
        amounts[subject.id] = plannedAmountsBySection[`subject-${subject.name}`] || 0;
      }
    });

    return amounts;
  }, [currentBudget, plannedAmountsBySection]);

  // Calculate amount per period from percentage - always calculate dynamically based on current settings
  // This ensures the displayed amount updates when you change: interval type, total income, or percentage
  const calcAmountPerPeriod = (percent: number): number => {
    if (!totalIncome || !periodsCount || !percent) return 0;
    return (totalIncome * percent / 100) / periodsCount;
  };

  const handleSaveName = () => {
    if (currentBudget && budgetName.trim()) {
      updateBudget(currentBudget.id, { name: budgetName });
    }
    setEditingName(false);
  };

  const handleAddSubject = (type: SubjectType) => {
    const name = prompt(t('settings.enterSubjectName'));
    if (!name) return;

    addSubject({
      name,
      type,
      order: currentBudget?.subjects.length || 0,
    });
  };

  const handleAddSection = (subjectId: string) => {
    const name = prompt(t('settings.enterSectionName') || 'Enter section name:');
    if (!name) return;

    addSection({
      name,
      subjectId,
      order: (sectionsBySubject[subjectId]?.length || 0),
    });
  };

  const handleUpdateSectionGoal = (sectionId: string, goal: number) => {
    updateSection(sectionId, { incomePartGoal: goal });
  };

  // Start editing a subject name
  const startEditingSubject = (subjectId: string, currentName: string) => {
    setEditingSubjectId(subjectId);
    setEditingSubjectName(currentName);
  };

  // Save subject name
  const saveSubjectName = () => {
    if (editingSubjectId && editingSubjectName.trim()) {
      updateSubject(editingSubjectId, { name: editingSubjectName.trim() });
    }
    setEditingSubjectId(null);
    setEditingSubjectName('');
  };

  // Start editing a section name
  const startEditingSection = (sectionId: string, currentName: string) => {
    setEditingSectionId(sectionId);
    setEditingSectionName(currentName);
  };

  // Save section name
  const saveSectionName = () => {
    if (editingSectionId && editingSectionName.trim()) {
      updateSection(editingSectionId, { name: editingSectionName.trim() });
    }
    setEditingSectionId(null);
    setEditingSectionName('');
  };

  // Move subject up in order
  const moveSubjectUp = (subjectId: string) => {
    if (!currentBudget) return;
    const subjects = [...currentBudget.subjects];
    const index = subjects.findIndex(s => s.id === subjectId);
    if (index <= 0) return; // Already at top

    // Swap with previous
    [subjects[index - 1], subjects[index]] = [subjects[index], subjects[index - 1]];
    // Update order values
    subjects.forEach((s, i) => s.order = i);
    updateBudget(currentBudget.id, { subjects });
  };

  // Move subject down in order
  const moveSubjectDown = (subjectId: string) => {
    if (!currentBudget) return;
    const subjects = [...currentBudget.subjects];
    const index = subjects.findIndex(s => s.id === subjectId);
    if (index < 0 || index >= subjects.length - 1) return; // Already at bottom

    // Swap with next
    [subjects[index], subjects[index + 1]] = [subjects[index + 1], subjects[index]];
    // Update order values
    subjects.forEach((s, i) => s.order = i);
    updateBudget(currentBudget.id, { subjects });
  };

  // Create a new budget
  const handleCreateNewBudget = async () => {
    const name = prompt(t('settings.enterBudgetName') || 'Enter budget name:');
    if (!name) return;

    try {
      // Create default structure for new budget
      const now = new Date();
      const startOfYear = new Date(now.getFullYear(), 0, 1);
      const endOfYear = new Date(now.getFullYear(), 11, 31);

      await createBudget({
        name,
        organizationId: '',
        intervalType: 'monthly',
        startDate: startOfYear.toISOString().slice(0, 10),
        endDate: endOfYear.toISOString().slice(0, 10),
        subjects: [
          { id: 'subject-income', name: 'הכנסות', type: 'credit', order: 0, goalPercentage: 0 },
          { id: 'subject-expenses', name: 'הוצאות', type: 'debit', order: 1 },
          { id: 'subject-profit', name: 'רווח', type: 'calculated', order: 2 },
        ],
        sections: [],
        measures: ['בפועל', 'מתוכנן'],
        currency: 'ILS',
        isActive: true,
      });
    } catch (error) {
      console.error('Error creating budget:', error);
      alert(t('settings.createBudgetError') || 'Error creating budget');
    }
  };

  const handleIntervalChange = (interval: IntervalType) => {
    if (currentBudget) {
      updateBudget(currentBudget.id, { intervalType: interval });
    }
  };

  const handleDeleteBudget = async () => {
    if (!currentBudget) return;
    if (confirm(t('settings.confirmDeleteBudget') || 'Are you sure you want to delete this budget?')) {
      await deleteBudget(currentBudget.id);
    }
  };

  const getSubjectTypeLabel = (type: SubjectType) => {
    switch (type) {
      case 'credit': return t('settings.credit') || 'זכות';
      case 'debit': return t('settings.debit') || 'חובה';
      case 'calculated': return t('settings.calculated') || 'מחושב';
      default: return type;
    }
  };

  if (!currentBudget) {
    return (
      <div className="settings-page">
        <h1>{t('nav.settings')}</h1>
        <div className="empty-state">
          <p>{t('budget.noBudgets')}</p>
        </div>
      </div>
    );
  }

  // Format date for HTML date input (YYYY-MM-DD)
  const formatDate = (date: Date | string | { toDate?: () => Date } | undefined): string => {
    if (!date) return '';

    // Handle Firestore Timestamp
    if (typeof date === 'object' && 'toDate' in date && typeof date.toDate === 'function') {
      return date.toDate().toISOString().slice(0, 10);
    }

    // Handle Date object
    if (date instanceof Date) {
      return date.toISOString().slice(0, 10);
    }

    // Handle string - try to parse and reformat
    if (typeof date === 'string') {
      // If already in YYYY-MM-DD format, return as is
      if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
        return date;
      }
      // Try to parse other date formats
      const parsed = new Date(date);
      if (!isNaN(parsed.getTime())) {
        return parsed.toISOString().slice(0, 10);
      }
    }

    return '';
  };

  return (
    <div className="settings-page" dir={direction}>
      {/* General Settings */}
      <section className="settings-section">
        <h2>{t('settings.general') || 'כללי'}</h2>
        <div className="general-settings">
          <div className="setting-row">
            <label>{t('settings.name') || 'שם'}:</label>
            {editingName ? (
              <div className="inline-edit">
                <input
                  type="text"
                  value={budgetName}
                  onChange={(e) => setBudgetName(e.target.value)}
                  onBlur={handleSaveName}
                  onKeyDown={(e) => e.key === 'Enter' && handleSaveName()}
                  autoFocus
                />
              </div>
            ) : (
              <span onClick={() => { setEditingName(true); setBudgetName(currentBudget.name); }} className="editable">
                {currentBudget.name}
              </span>
            )}
          </div>

          <div className="setting-row">
            <label>{t('settings.intervalType') || 'סוג מרווח'}:</label>
            <div className="button-group">
              <button
                className={`btn-toggle ${currentBudget.intervalType === 'weekly' ? 'active' : ''}`}
                onClick={() => handleIntervalChange('weekly')}
              >
                {t('settings.weekly')}
              </button>
              <button
                className={`btn-toggle ${currentBudget.intervalType === 'monthly' ? 'active' : ''}`}
                onClick={() => handleIntervalChange('monthly')}
              >
                {t('settings.monthly')}
              </button>
            </div>
          </div>

          <div className="setting-row">
            <label>{t('settings.startDate') || 'תאריך התחלה'}:</label>
            <input
              type="date"
              value={formatDate(currentBudget.startDate)}
              onChange={(e) => updateBudget(currentBudget.id, { startDate: e.target.value })}
            />
          </div>

          <div className="setting-row">
            <label>{t('settings.endDate') || 'תאריך סיום'}:</label>
            <input
              type="date"
              value={formatDate(currentBudget.endDate)}
              onChange={(e) => updateBudget(currentBudget.id, { endDate: e.target.value })}
            />
          </div>

          <div className="setting-row">
            <label>{t('settings.currency') || 'מטבע'}:</label>
            <select
              className="currency-selector"
              value={currentBudget.currency || 'ILS'}
              onChange={(e) => updateBudget(currentBudget.id, { currency: e.target.value })}
            >
              <option value="ILS">₪ ILS (שקל)</option>
              <option value="USD">$ USD (דולר)</option>
              <option value="EUR">€ EUR (אירו)</option>
              <option value="GBP">£ GBP (לירה)</option>
            </select>
          </div>
        </div>
      </section>

      {/* Subjects with Sections and Goals side by side */}
      <section className="settings-section">
        <div className="section-header-row">
          <h2>{t('settings.subjects') || 'נושאים'}</h2>
          <h2 className="goals-header">{t('settings.goals') || 'יעדים'}</h2>
        </div>
        <div className="subjects-tree">
          {currentBudget.subjects.map((subject) => {
            const totalDebitGoals = currentBudget.subjects
              .filter(s => s.type === 'debit')
              .reduce((sum, s) => sum + (subjectGoalTotals[s.id] || 0), 0);
            const calculatedGoal = subject.type === 'calculated' ? 100 - totalDebitGoals : null;

            return (
              <div key={subject.id} className={`subject-node type-${subject.type}`}>
                <div className="subject-row">
                  <div className="subject-header">
                    {editingSubjectId === subject.id ? (
                      <input
                        type="text"
                        className="inline-edit-input"
                        value={editingSubjectName}
                        onChange={(e) => setEditingSubjectName(e.target.value)}
                        onBlur={saveSubjectName}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') saveSubjectName();
                          if (e.key === 'Escape') { setEditingSubjectId(null); setEditingSubjectName(''); }
                        }}
                        autoFocus
                      />
                    ) : (
                      <span
                        className="subject-name editable-name"
                        onClick={() => startEditingSubject(subject.id, subject.name)}
                        title={t('actions.edit') || 'לחץ לעריכה'}
                      >
                        {subject.name} ({getSubjectTypeLabel(subject.type)})
                      </span>
                    )}
                    <div className="reorder-buttons">
                      <button
                        className="btn-icon btn-reorder"
                        onClick={() => moveSubjectUp(subject.id)}
                        disabled={currentBudget.subjects.indexOf(subject) === 0}
                        title={t('actions.moveUp') || 'הזז למעלה'}
                      >
                        ▲
                      </button>
                      <button
                        className="btn-icon btn-reorder"
                        onClick={() => moveSubjectDown(subject.id)}
                        disabled={currentBudget.subjects.indexOf(subject) === currentBudget.subjects.length - 1}
                        title={t('actions.moveDown') || 'הזז למטה'}
                      >
                        ▼
                      </button>
                    </div>
                    <button
                      className="btn-icon"
                      onClick={() => handleAddSection(subject.id)}
                      title={t('settings.addSection') || 'הוסף סעיף'}
                    >
                      +
                    </button>
                    <button
                      className="btn-icon btn-danger"
                      onClick={() => {
                        if (confirm(t('actions.confirmDelete'))) {
                          removeSubject(subject.id);
                        }
                      }}
                      title={t('actions.delete')}
                    >
                      ×
                    </button>
                  </div>
                  <div className={`goal-column ${subject.type === 'credit' ? 'income-column' : ''}`}>
                    {subject.type === 'credit' && (
                      <div className="income-input-wrapper">
                        <span className="currency-symbol">{getCurrencySymbol(currentBudget.currency)}</span>
                        <input
                          type="number"
                          className="income-input"
                          step="1"
                          value={getEditingValue(`income-${subject.id}`, subject.goalPercentage || totalIncome)}
                          onChange={(e) => setEditingValue(`income-${subject.id}`, e.target.value)}
                          onBlur={(e) => {
                            const value = parseFloat(e.target.value) || 0;
                            if (value !== subject.goalPercentage) {
                              updateSubject(subject.id, { goalPercentage: value });
                            }
                            clearEditingValue(`income-${subject.id}`);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              (e.target as HTMLInputElement).blur();
                            }
                          }}
                          placeholder="0"
                        />
                      </div>
                    )}
                    {subject.type === 'debit' && (
                      <div className="goal-with-amount">
                        <span className="goal-total">{formatNumber(subjectGoalTotals[subject.id])}%</span>
                        <span className="calculated-amount">
                          {getCurrencySymbol(currentBudget.currency)}{formatAmount(calcAmountPerPeriod(subjectGoalTotals[subject.id] || 0))}/{currentBudget.intervalType === 'weekly' ? 'שבוע' : 'חודש'}
                        </span>
                      </div>
                    )}
                    {subject.type === 'calculated' && (
                      <div className="goal-with-amount">
                        <span className="goal-total calculated">{formatNumber(calculatedGoal || 0)}%</span>
                        <span className="calculated-amount">
                          {getCurrencySymbol(currentBudget.currency)}{formatAmount(calcAmountPerPeriod(calculatedGoal || 0))}/{currentBudget.intervalType === 'weekly' ? 'שבוע' : 'חודש'}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="sections-list">
                  {(sectionsBySubject[subject.id] || []).map((section) => (
                    <div key={section.id} className="section-row">
                      <div className="section-item">
                        {editingSectionId === section.id ? (
                          <input
                            type="text"
                            className="inline-edit-input section-edit"
                            value={editingSectionName}
                            onChange={(e) => setEditingSectionName(e.target.value)}
                            onBlur={saveSectionName}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') saveSectionName();
                              if (e.key === 'Escape') { setEditingSectionId(null); setEditingSectionName(''); }
                            }}
                            autoFocus
                          />
                        ) : (
                          <span
                            className="section-name editable-name"
                            onClick={() => startEditingSection(section.id, section.name)}
                            title={t('actions.edit') || 'לחץ לעריכה'}
                          >
                            {section.name}
                          </span>
                        )}
                        <button
                          className="btn-icon btn-danger"
                          onClick={() => {
                            if (confirm(t('actions.confirmDelete'))) {
                              removeSection(section.id);
                            }
                          }}
                        >
                          ×
                        </button>
                      </div>
                      <div className="goal-column">
                        <div className="goal-with-amount">
                          <div className="percent-input-wrapper">
                            <input
                              type="number"
                              step="1"
                              value={getEditingValue(`section-${section.id}`, section.incomePartGoal)}
                              onChange={(e) => setEditingValue(`section-${section.id}`, e.target.value)}
                              onBlur={(e) => {
                                const value = parseFloat(e.target.value) || 0;
                                if (value !== section.incomePartGoal) {
                                  handleUpdateSectionGoal(section.id, value);
                                }
                                clearEditingValue(`section-${section.id}`);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  (e.target as HTMLInputElement).blur();
                                }
                              }}
                              placeholder="0"
                            />
                            <span className="percent-symbol">%</span>
                          </div>
                          <span className="calculated-amount">
                            {getCurrencySymbol(currentBudget.currency)}{formatAmount(calcAmountPerPeriod(parseFloat(getEditingValue(`section-${section.id}`, section.incomePartGoal)) || 0))}/{currentBudget.intervalType === 'weekly' ? 'שבוע' : 'חודש'}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                  {(!sectionsBySubject[subject.id] || sectionsBySubject[subject.id].length === 0) && (
                    <div className="section-row">
                      <div className="no-sections">
                        <button className="btn-link" onClick={() => handleAddSection(subject.id)}>
                          + {t('settings.addFirstSection') || 'הוסף סעיף ראשון'}
                        </button>
                      </div>
                      <div className="goal-column"></div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          <button className="btn btn-add-subject" onClick={() => handleAddSubject('debit')}>
            + {t('settings.addSubject')}
          </button>
        </div>
      </section>

      {/* Measures */}
      <section className="settings-section">
        <h2>{t('settings.measures') || 'מדדים'}</h2>
        <div className="measures-list">
          {currentBudget.measures.map((measure, idx) => (
            <div key={idx} className="measure-item">
              <span>{measure === 'budget' ? 'מתוכנן' : measure === 'actual' ? 'בפועל' : measure}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Action Buttons */}
      <section className="settings-section actions-section">
        <button className="btn btn-success" onClick={handleCreateNewBudget}>
          {t('settings.createNewBudget') || '+ תקציב חדש'}
        </button>
        <button className="btn btn-primary" onClick={() => alert('Budget updated')}>
          {t('settings.updateBudget') || 'עדכן תקציב'}
        </button>
        <button className="btn btn-secondary" onClick={async () => {
          try {
            await createDataCollection();
            navigate('/data');
          } catch (error) {
            console.error('Error creating data collection:', error);
            alert(t('settings.dataCollectionError') || 'Error creating data collection');
          }
        }}>
          {t('settings.createDataCollection') || 'צור אוסף נתונים'}
        </button>
        <button className="btn btn-danger" onClick={handleDeleteBudget}>
          {t('settings.deleteBudget') || 'מחק תקציב'}
        </button>
        <button className="btn btn-secondary" onClick={() => alert('Duplicate')}>
          {t('settings.duplicate') || 'שכפול'}
        </button>
      </section>

      <style>{`
        .settings-page {
          max-width: 900px;
          margin: 0 auto;
        }

        .settings-section {
          background: var(--color-surface);
          border-radius: var(--radius-lg);
          padding: 1.5rem;
          margin-bottom: 1.5rem;
          box-shadow: var(--shadow);
        }

        .settings-section h2 {
          margin: 0 0 1rem;
          font-size: 1.125rem;
          border-bottom: 1px solid var(--color-border);
          padding-bottom: 0.5rem;
        }

        .general-settings {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .setting-row {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .setting-row label {
          min-width: 120px;
          font-weight: 500;
        }

        .setting-row input[type="text"],
        .setting-row input[type="date"],
        .setting-row select {
          padding: 0.5rem 0.75rem;
          border: 1px solid var(--color-border);
          border-radius: var(--radius);
          font-size: 0.875rem;
        }

        .currency-selector {
          min-width: 150px;
        }

        .editable {
          cursor: pointer;
          padding: 0.25rem 0.5rem;
          border-radius: var(--radius);
        }

        .editable:hover {
          background: var(--color-background);
        }

        .button-group {
          display: flex;
          gap: 0.25rem;
        }

        .btn-toggle {
          padding: 0.5rem 1rem;
          border: 1px solid var(--color-border);
          background: var(--color-surface);
          cursor: pointer;
          font-size: 0.875rem;
        }

        .btn-toggle:first-child {
          border-radius: var(--radius) 0 0 var(--radius);
        }

        .btn-toggle:last-child {
          border-radius: 0 var(--radius) var(--radius) 0;
        }

        .btn-toggle.active {
          background: var(--color-primary);
          color: white;
          border-color: var(--color-primary);
        }

        /* Section Header Row */
        .section-header-row {
          display: flex;
          align-items: center;
          margin-bottom: 1rem;
          border-bottom: 1px solid var(--color-border);
          padding-bottom: 0.5rem;
        }

        .section-header-row h2 {
          margin: 0;
          border: none;
          padding: 0;
        }

        .section-header-row h2:first-child {
          flex: 1;
        }

        .goals-header {
          width: 140px;
          text-align: center;
          background: #f0f9ff;
          padding: 0.25rem 0.5rem;
          border-radius: var(--radius);
          font-size: 0.875rem;
        }

        /* Subjects Tree */
        .subjects-tree {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .subject-node {
          border: 1px solid var(--color-border);
          border-radius: var(--radius);
          overflow: hidden;
        }

        .subject-node.type-credit {
          border-color: #22c55e;
        }

        .subject-node.type-debit {
          border-color: #ef4444;
        }

        .subject-node.type-calculated {
          border-color: #3b82f6;
        }

        .subject-row, .section-row {
          display: flex;
          align-items: center;
        }

        .subject-header {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem;
          background: var(--color-background);
          font-weight: 500;
          flex: 1;
        }

        .subject-name {
          flex: 1;
        }

        .editable-name {
          cursor: pointer;
          padding: 0.25rem 0.5rem;
          border-radius: var(--radius);
          transition: background 0.15s;
        }

        .editable-name:hover {
          background: rgba(0, 0, 0, 0.05);
        }

        .inline-edit-input {
          flex: 1;
          padding: 0.25rem 0.5rem;
          border: 1px solid var(--color-primary);
          border-radius: var(--radius);
          font-size: inherit;
          font-weight: inherit;
          outline: none;
        }

        .inline-edit-input.section-edit {
          font-weight: 400;
        }

        .goal-column {
          width: 140px;
          padding: 0.5rem;
          background: #f0f9ff;
          display: flex;
          align-items: center;
          justify-content: center;
          border-right: 1px solid #e0f2fe;
        }

        .goal-column.income-column {
          width: 420px;
          background: #ecfdf5;
        }

        .goal-column input {
          width: 60px;
          padding: 0.25rem 0.5rem;
          border: 1px solid var(--color-border);
          border-radius: var(--radius);
          text-align: center;
          font-size: 0.875rem;
        }

        .income-input-wrapper {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .income-input-wrapper .income-input {
          width: 350px;
          text-align: right;
          font-size: 1.125rem;
          font-weight: 600;
        }

        .currency-symbol {
          font-weight: 600;
          color: #166534;
          font-size: 0.875rem;
        }

        .percent-input-wrapper {
          display: flex;
          align-items: center;
          gap: 0.125rem;
        }

        .percent-input-wrapper input {
          width: 50px;
        }

        .percent-symbol {
          font-size: 0.75rem;
          color: var(--color-text-secondary);
        }

        .section-percent {
          font-size: 0.875rem;
          color: var(--color-text-secondary);
        }

        .goal-with-amount {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.25rem;
        }

        .calculated-amount {
          font-size: 0.7rem;
          color: #059669;
          font-weight: 500;
          white-space: nowrap;
        }

        .goal-total {
          font-weight: 600;
          color: var(--color-text-secondary);
        }

        .goal-total.calculated {
          color: #3b82f6;
        }

        .sections-list {
          padding: 0;
        }

        .section-row {
          border-top: 1px solid var(--color-border);
        }

        .section-item {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 0.75rem 0.5rem 2rem;
          flex: 1;
        }

        .section-name {
          flex: 1;
        }

        .no-sections {
          color: var(--color-text-secondary);
          font-size: 0.875rem;
          padding: 0.5rem 0.75rem 0.5rem 2rem;
          flex: 1;
        }

        .btn-icon {
          width: 24px;
          height: 24px;
          padding: 0;
          border: 1px solid var(--color-border);
          border-radius: var(--radius);
          background: var(--color-surface);
          cursor: pointer;
          font-size: 1rem;
          line-height: 1;
        }

        .btn-icon:hover {
          background: var(--color-background);
        }

        .btn-icon.btn-danger:hover {
          background: #fee2e2;
          border-color: #ef4444;
          color: #ef4444;
        }

        .reorder-buttons {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .btn-reorder {
          width: 20px;
          height: 16px;
          font-size: 0.625rem;
          color: var(--color-text-secondary);
        }

        .btn-reorder:disabled {
          opacity: 0.3;
          cursor: not-allowed;
        }

        .btn-reorder:not(:disabled):hover {
          background: var(--color-primary);
          color: white;
          border-color: var(--color-primary);
        }

        .btn-link {
          background: none;
          border: none;
          color: var(--color-primary);
          cursor: pointer;
          padding: 0;
          font-size: 0.875rem;
        }

        .btn-add-subject {
          margin-top: 0.5rem;
        }

        /* Measures */
        .measures-list {
          display: flex;
          gap: 0.5rem;
        }

        .measure-item {
          padding: 0.5rem 1rem;
          background: var(--color-background);
          border-radius: var(--radius);
          font-size: 0.875rem;
        }

        /* Actions */
        .actions-section {
          display: flex;
          flex-wrap: wrap;
          gap: 0.75rem;
        }

        .btn {
          padding: 0.5rem 1rem;
          border: 1px solid var(--color-border);
          border-radius: var(--radius);
          cursor: pointer;
          font-size: 0.875rem;
        }

        .btn-primary {
          background: var(--color-primary);
          color: white;
          border-color: var(--color-primary);
        }

        .btn-secondary {
          background: var(--color-surface);
        }

        .btn-danger {
          background: #fee2e2;
          color: #991b1b;
          border-color: #ef4444;
        }

        .btn-success {
          background: #22c55e;
          color: white;
          border-color: #22c55e;
        }

        .btn-success:hover {
          background: #16a34a;
          border-color: #16a34a;
        }

        .empty-state {
          text-align: center;
          padding: 2rem;
          color: var(--color-text-secondary);
        }
      `}</style>
    </div>
  );
};
